import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { array, TArrayCodec } from "../../../shared/src/codecs/types/array";
import { dateTime } from "../../../shared/src/codecs/types/dateTime";
import { form, TFormCodec } from "../../../shared/src/codecs/types/form";
import { intersection } from "../../../shared/src/codecs/types/intersection";
import { literal } from "../../../shared/src/codecs/types/literal";
import { nullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { union } from "../../../shared/src/codecs/types/union";
import { uuid, TAnyUuidCodec } from "../../../shared/src/codecs/types/uuid";
import { StaffUser2, TStaffUser2Codec } from "../User";
import { ChatsMessageForm, TChatsMessageFormCodec } from "./ChatsMessageForm";
import {
    ChatAssignmentAction,
    ChatAwaitAction,
    ChatMessageAction,
    RoadblockAwaitableEntity,
    RoadblockCheckItemAction,
    RoadblockClosedAction,
    RoadblockOpenAction,
    RoadblockStatus,
    RoadblockType,
    SignOffInvalidationAction,
    TRoadblockAction,
    TRoadblockAwaitableEntityCodec,
} from "./RoadblockForm";

export const CaseTimelineRoadblockContext = required({
    roadblock_id: uuid(),
    roadblock_type: RoadblockType,
    chats_id: uuid(),
});
export type TCaseTimelineRoadblockContextCodec = typeof CaseTimelineRoadblockContext;
export type TCaseTimelineRoadblockContext = TTypeOfCodec<TCaseTimelineRoadblockContextCodec>;

export const CaseMilestoneType = union([
    literal("instructed"),
    literal("exchanged"),
    literal("completed"),
    literal("abandoned"),
]);
export type TCaseMilestoneTypeCodec = typeof CaseMilestoneType;
export type TCaseMilestoneType = TTypeOfCodec<TCaseMilestoneTypeCodec>;

export const CaseMilestoneAction = required({
    tag: literal("milestone"),
    action_date: dateTime(),
    action: required({
        milestone_type: CaseMilestoneType,
        created_by: nullCodec(),
    }),
});
export type TCaseMilestoneActionCodec = typeof CaseMilestoneAction;
export type TCaseMilestoneAction = TTypeOfCodec<TCaseMilestoneActionCodec>;

const RoadblockContextWrap = required({
    roadblock: CaseTimelineRoadblockContext,
});

const MilestoneContextWrap = required({
    roadblock: nullCodec(),
});

export const CaseTimelineEntry = union([
    intersection([ChatMessageAction, RoadblockContextWrap]),
    intersection([SignOffInvalidationAction, RoadblockContextWrap]),
    intersection([ChatAssignmentAction, RoadblockContextWrap]),
    intersection([ChatAwaitAction, RoadblockContextWrap]),
    intersection([RoadblockOpenAction, RoadblockContextWrap]),
    intersection([RoadblockClosedAction, RoadblockContextWrap]),
    intersection([RoadblockCheckItemAction, RoadblockContextWrap]),
    intersection([CaseMilestoneAction, MilestoneContextWrap]),
]);
export type TCaseTimelineEntryCodec = typeof CaseTimelineEntry;
export type TCaseTimelineEntry = TTypeOfCodec<TCaseTimelineEntryCodec>;

export type TCaseTimelineMilestoneEntry = TCaseMilestoneAction & { roadblock: null };
export type TCaseTimelineRoadblockEntry = TRoadblockAction & { roadblock: TCaseTimelineRoadblockContext };

export const isCaseTimelineMilestoneEntry = (entry: TCaseTimelineEntry): entry is TCaseTimelineMilestoneEntry =>
    entry.tag === "milestone";
export const isCaseTimelineRoadblockEntry = (entry: TCaseTimelineEntry): entry is TCaseTimelineRoadblockEntry =>
    entry.tag !== "milestone";

export const CaseTimelineRoadblockOption = required({
    id: uuid(),
    type: RoadblockType,
    status: RoadblockStatus,
    chats_id: uuid(),
    last_action_at: dateTime(),
});
export type TCaseTimelineRoadblockOptionCodec = typeof CaseTimelineRoadblockOption;
export type TCaseTimelineRoadblockOption = TTypeOfCodec<TCaseTimelineRoadblockOptionCodec>;

export const CaseTimelineForm: TFormCodec<
    TRequiredCodec<{
        case_id: TAnyUuidCodec,
    }>,
    TRequiredCodec<{
        entries: TArrayCodec<TCaseTimelineEntryCodec>,
        participants: TArrayCodec<TStaffUser2Codec>,
        awaitable_entities: TArrayCodec<TRoadblockAwaitableEntityCodec>,
        roadblock_options: TArrayCodec<TCaseTimelineRoadblockOptionCodec>,
        chat_message_form: TChatsMessageFormCodec,
        current_user_id: TAnyUuidCodec,
    }>
> = form(
    required({
        case_id: uuid(),
    }),
    required({
        entries: array(CaseTimelineEntry),
        participants: array(StaffUser2),
        awaitable_entities: array(RoadblockAwaitableEntity),
        roadblock_options: array(CaseTimelineRoadblockOption),
        chat_message_form: ChatsMessageForm,
        current_user_id: uuid(),
    }),
);
export type TCaseTimelineFormCodec = typeof CaseTimelineForm;
export type TCaseTimelineForm = TTypeOfCodec<TCaseTimelineFormCodec>;
